import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Bot, Copy, Power, PowerOff, Lock, Trash2, ShieldCheck, ShieldOff, Eye, EyeOff,
  AlertTriangle,
} from "lucide-react";
import { formatDay } from "@/lib/format";
import { usePipelineCtx } from "@/lib/pipelineContext.jsx";
import { useTerm } from "@/lib/terminology.jsx";

const AGENT_TYPE_LABELS = {
  research: "Research",
  scoring: "Scoring",
  summary: "Summary",
  custom: "Custom",
};

const PROVIDER_LABELS = {
  anthropic: "Claude",
  openai: "OpenAI",
  gemini: "Gemini",
};

export default function AgentLibrary() {
  const nav = useNavigate();
  const { pipelineId } = usePipelineCtx();
  const term = useTerm();
  const [agents, setAgents] = useState([]);
  const [showHidden, setShowHidden] = useState(false);
  const [filter, setFilter] = useState("all"); // "all" | "needs_attention"
  const load = () => {
    if (!pipelineId) return;
    api.get("/v61/agents", { params: { pipeline_id: pipelineId, include_hidden: showHidden } })
      .then(({ data }) => setAgents(data));
  };
  useEffect(() => { load(); }, [pipelineId, showHidden]); // eslint-disable-line react-hooks/exhaustive-deps

  const publish = async (a) => {
    if (a.is_hidden) {
      toast.error("Unhide this agent before publishing.");
      return;
    }
    if (!window.confirm("Publishing locks this agent's steps and scope. Name and description can still be edited. Confirm publish?")) return;
    try {
      await api.post(`/agents/${a.agent_id}/publish`);
      toast.success("Agent published");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };
  const unpublish = async (a) => {
    if (!window.confirm("Unpublish this agent? It will be removed from the picker for new Agent Runs. Existing run history is unaffected.")) return;
    try {
      await api.post(`/agents/${a.agent_id}/unpublish`);
      toast.success("Agent unpublished");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };
  const toggle = async (a) => {
    await api.post(`/agents/${a.agent_id}/toggle`);
    toast.success(a.is_active ? "Agent disabled" : "Agent enabled");
    load();
  };
  const duplicate = async (a) => {
    const { data } = await api.post(`/agents/${a.agent_id}/duplicate`);
    toast.success("Agent duplicated");
    nav(`/agents/${data.agent_id}`);
  };
  const remove = async (a) => {
    if (!window.confirm(`Delete draft agent "${a.agent_name}"?`)) return;
    try {
      await api.delete(`/agents/${a.agent_id}`);
      toast.success("Agent deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };
  const hide = async (a) => {
    await api.post(`/agents/${a.agent_id}/hide`);
    toast.success("Agent hidden");
    load();
  };
  const unhide = async (a) => {
    await api.post(`/agents/${a.agent_id}/unhide`);
    toast.success("Agent unhidden");
    load();
  };

  const visible = agents
    .filter((a) => showHidden || !a.is_hidden)
    .filter((a) => filter === "all" || a.is_needs_attention);
  const needsAttentionCount = agents.filter((a) => a.is_needs_attention).length;

  // For the Needs-Attention filter, detect shared broken mappings so a
  // Bulk Remap CTA appears above the list.
  const sharedBrokenCombos = (() => {
    if (filter !== "needs_attention") return [];
    const map = {};
    agents.filter((a) => a.is_needs_attention).forEach((a) => {
      const key = `${a.provider_id || a.model_provider || "_"}||${a.model_id || a.model_name || "_"}`;
      map[key] = map[key] || {
        provider_id: a.provider_id, model_id: a.model_id || a.model_name, count: 0,
      };
      map[key].count += 1;
    });
    return Object.values(map).filter((c) => c.count >= 2 && c.provider_id && c.model_id);
  })();

  return (
    <div className="p-8">
      <div className="flex items-start justify-between">
        <div>
          <div className="cw-caption uppercase tracking-widest">{term("Agent")} Library</div>
          <h1 className="cw-h1 mt-1 inline-flex items-center gap-2">
            <Bot size={26} /> AI {term("Agents")}
          </h1>
          <p className="text-sm text-[var(--cw-muted)] mt-1">
            Configure LLM-powered {term("agents").toLowerCase()} to analyze {term("deals").toLowerCase()} — research, scoring, or summaries.
          </p>
        </div>
        <button onClick={() => nav("/agents/new")}
          className="cw-btn-primary flex items-center gap-2" data-testid="new-agent-btn">
          <Plus size={16} /> New {term("Agent")}
        </button>
      </div>

      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <div
          className="inline-flex rounded-md border overflow-hidden"
          style={{ borderColor: "var(--cw-border)" }}
        >
          {[
            { k: "all", l: "All" },
            { k: "needs_attention", l: `Needs Attention${needsAttentionCount ? ` (${needsAttentionCount})` : ""}` },
          ].map((b) => (
            <button
              key={b.k}
              onClick={() => setFilter(b.k)}
              className={`text-xs px-3 py-1.5 ${filter === b.k ? "font-semibold" : "text-[var(--cw-muted)]"}`}
              style={filter === b.k ? { background: "rgba(40,42,116,0.08)", color: "var(--cw-primary)" } : {}}
              data-testid={`agents-filter-${b.k}`}
            >
              {b.l}
            </button>
          ))}
        </div>
        <label className="text-xs inline-flex items-center gap-2">
          <input type="checkbox" checked={showHidden}
            onChange={(e) => setShowHidden(e.target.checked)}
            data-testid="agents-show-hidden" />
          Show hidden {term("agents").toLowerCase()}
        </label>
        <button
          onClick={() => nav("/bulk-remap")}
          className="text-xs px-3 py-1.5 rounded-md border ml-auto"
          style={{ borderColor: "var(--cw-border)" }}
          data-testid="agents-bulk-remap-btn"
        >
          Bulk Remap
        </button>
      </div>

      {sharedBrokenCombos.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="agents-shared-broken">
          {sharedBrokenCombos.map((c) => (
            <div key={`${c.provider_id}-${c.model_id}`}
              className="cw-card p-3 flex items-center gap-3 flex-wrap"
              style={{ background: "rgba(215,60,60,0.04)" }}>
              <AlertTriangle size={14} className="text-[#B02B2B]" />
              <div className="text-sm flex-1">
                <strong>{c.count}</strong> {term("agents").toLowerCase()} share the same broken mapping —
                <code className="ml-1">{c.model_id}</code>
              </div>
              <button
                onClick={() => nav(`/bulk-remap?src_provider=${c.provider_id}&src_model=${encodeURIComponent(c.model_id)}`)}
                className="cw-btn-primary text-xs inline-flex items-center gap-1"
                data-testid={`bulk-remap-shared-${c.model_id}`}
              >
                Bulk Remap
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 grid md:grid-cols-2 gap-4">
        {visible.map((a) => (
          <div key={a.agent_id} className="cw-card p-5"
            style={{ opacity: a.is_hidden ? 0.55 : 1 }}
            data-testid={`agent-card-${a.agent_name}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-base">{a.agent_name}</h3>
                  {a.is_published ? (
                    <span className="cw-badge" style={{ background: "rgba(44,154,82,0.12)", color: "#1F6E3A" }}>
                      <Lock size={10} /> Published
                    </span>
                  ) : (
                    <span className="cw-badge" style={{ background: "rgba(224,161,0,0.12)", color: "#7A5900" }}>
                      Draft
                    </span>
                  )}
                  {a.is_hidden && (
                    <span className="cw-badge" style={{ background: "rgba(0,0,0,0.08)", color: "#666" }}>
                      Hidden
                    </span>
                  )}
                  {!a.is_active && (
                    <span className="cw-badge" style={{ background: "rgba(0,0,0,0.06)", color: "#666" }}>
                      Disabled
                    </span>
                  )}
                  {a.is_needs_attention && (
                    <span
                      className="cw-badge inline-flex items-center gap-1"
                      style={{ background: "rgba(215,60,60,0.12)", color: "#B02B2B" }}
                      data-testid={`needs-attention-${a.agent_name}`}
                      title={reasonLabel(a.needs_attention_reason)}
                    >
                      <AlertTriangle size={10} /> Needs Attention
                    </span>
                  )}
                </div>
                <div className="text-xs text-[var(--cw-muted)] mt-1">
                  {AGENT_TYPE_LABELS[a.agent_type] || a.agent_type} · {PROVIDER_LABELS[a.model_provider]} · {a.model_name}
                </div>
                {a.description && (
                  <p className="text-sm text-[var(--cw-muted)] mt-2 line-clamp-2">{a.description}</p>
                )}
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <button onClick={() => nav(`/agents/${a.agent_id}`)}
                className="cw-btn-ghost text-xs" data-testid={`open-agent-${a.agent_name}`}>
                {a.is_published ? "View / Edit name" : "Edit"}
              </button>
              {!a.is_published && !a.is_hidden && (
                <button onClick={() => publish(a)}
                  className="cw-btn-primary text-xs inline-flex items-center gap-1"
                  data-testid={`publish-agent-${a.agent_name}`}>
                  <ShieldCheck size={12} /> Publish
                </button>
              )}
              {a.is_published && (
                <button onClick={() => unpublish(a)}
                  className="cw-btn-ghost text-xs inline-flex items-center gap-1"
                  data-testid={`unpublish-agent-${a.agent_name}`}>
                  <ShieldOff size={12} /> Unpublish
                </button>
              )}
              <button onClick={() => duplicate(a)} className="cw-btn-ghost text-xs inline-flex items-center gap-1">
                <Copy size={12} /> Duplicate
              </button>
              <button onClick={() => toggle(a)} className="cw-btn-ghost text-xs inline-flex items-center gap-1">
                {a.is_active ? <><PowerOff size={12} /> Disable</> : <><Power size={12} /> Enable</>}
              </button>
              {!a.is_published && !a.is_hidden && (
                <button onClick={() => hide(a)} className="cw-btn-ghost text-xs inline-flex items-center gap-1"
                  data-testid={`hide-agent-${a.agent_name}`}>
                  <EyeOff size={12} /> Hide
                </button>
              )}
              {a.is_hidden && (
                <button onClick={() => unhide(a)} className="cw-btn-ghost text-xs inline-flex items-center gap-1"
                  data-testid={`unhide-agent-${a.agent_name}`}>
                  <Eye size={12} /> Unhide
                </button>
              )}
              {!a.is_published && (
                <button onClick={() => remove(a)}
                  className="text-xs inline-flex items-center gap-1 rounded-md px-3 py-1.5 border transition"
                  style={{ borderColor: "var(--cw-error)", color: "var(--cw-error)" }}>
                  <Trash2 size={12} />
                </button>
              )}
            </div>
            <div className="cw-caption mt-3">Created {formatDay(a.created_at)}</div>
          </div>
        ))}
        {visible.length === 0 && (
          <div className="col-span-full cw-card p-10 text-center text-[var(--cw-muted)]">
            {filter === "needs_attention"
              ? `Nothing needs attention — every ${term("agent").toLowerCase()} is connected to a valid provider.`
              : `No ${term("agents").toLowerCase()} yet. Click New ${term("Agent")} to create one.`}
          </div>
        )}
      </div>
    </div>
  );
}

function reasonLabel(code) {
  switch (code) {
    case "no_credential": return "No API key configured for this provider";
    case "invalid_credential": return "API key on file was rejected";
    case "quota_exceeded": return "Billing / quota issue on the provider account";
    case "model_missing": return "Selected model is no longer available on this key";
    default: return "Attention required";
  }
}
